import React, { useState, useEffect, useRef } from 'react';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import { FiMapPin, FiLink, FiPlus, FiX, FiSearch } from 'react-icons/fi';
import { FaInstagram, FaFacebook, FaSnapchat } from 'react-icons/fa';
import CustomSelectStatus from '../../base_component/CustomSelectStatus/CustomSelectStatus';
import EllipsisLoader from '../../base_component/EllipsisLoader/EllipsisLoader';
import { GoogleMap, Marker, useJsApiLoader } from "@react-google-maps/api";
import Autocomplete from 'react-google-autocomplete';
import { getProfileAPI, updateStudioAPI } from '../../service/ApiServices';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { FaXTwitter } from 'react-icons/fa6';

const GOOGLE_MAPS_API_KEY = "AIzaSyC9G8OXUWRk3F5Bv6WaH6atdW55_6wHmZ8";
const GOOGLE_MAPS_LIBRARIES = ['places'];

const studioValidationSchema = Yup.object({
    studioName: Yup.string()
        .required('Studio Name is required')
        .min(2, 'Studio Name must be at least 2 characters')
        .max(100, 'Studio Name must be 100 characters or less'),
    status: Yup.string()
        .required('Status is required'),
    location: Yup.string()
        .nullable(),
    buzzerNumber: Yup.string()
        .required('Buzzer Number is required')
        .min(2, 'Buzzer Number must be at least 2 characters')
        .max(50, 'Banner Number must be 50 characters or less'),
    about: Yup.string()
        .required('About is required')
        .min(10, 'About must be at least 10 characters')
        .max(1000, 'About must be 1000 characters or less'),
    socials: Yup.array().of(
        Yup.object().shape({
            url: Yup.string().url('Must be a valid URL').nullable()
        })
    )
});

const getSocialIcon = (platform) => {
    switch (platform) {
        case 'instagram': return FaInstagram;
        case 'facebook': return FaFacebook;
        case 'X': return FaXTwitter;
        case 'snapchat': return FaSnapchat;
        default: return FiLink;
    }
};

const isGoogleMapsReady = () => {
    return window.google &&
        window.google.maps &&
        window.google.maps.Geocoder &&
        typeof window.google.maps.Geocoder === 'function';
};

const getInitialValues = (studioData) => {
    const socialLinks = studioData?.social_links || [];
    const socials = socialLinks.map(link => ({
        platform: link.platform,
        url: link.url,
        icon: getSocialIcon(link.platform)
    }));

    const defaultSocials = [
        { platform: 'instagram', url: '', icon: FaInstagram },
        { platform: 'facebook', url: '', icon: FaFacebook },
        { platform: 'X', url: '', icon: FaXTwitter },
        { platform: 'snapchat', url: '', icon: FaSnapchat }
    ];

    const allSocials = [...socials];
    defaultSocials.forEach(defaultSocial => {
        if (!allSocials.find(social => social.platform === defaultSocial.platform)) {
            allSocials.push(defaultSocial);
        }
    });

    return {
        studioName: studioData?.studio_name || '',
        status: studioData?.studio_status?.value || '',
        location: studioData?.studio_street || '',
        buzzerNumber: studioData?.studio_buzzer_number || '',
        about: studioData?.studio_mission || '',
        socials: allSocials
    };
};

const LocationModal = ({ isOpen, onClose, onLocationSelect, initialAddress, canterlocatlon, centerlocationpin }) => {
    const mapRef = useRef(null);
    const formikRef = useRef(null);
    const [selectedLocation, setSelectedLocation] = useState(null);

    const addressValidationSchema = Yup.object({
        streetAddress: Yup.string()
            .required('Street Address is required')
            .min(2, 'Street Address must be at least 2 characters'),
        city: Yup.string()
            .required('City is required')
            .min(2, 'City must be at least 2 characters'),
        state: Yup.string()
            .required('State is required')
            .min(2, 'State must be at least 2 characters'),
        zipCode: Yup.string()
            .required('Zip Code is required'),
    });

    const initialAddressValues = {
        streetAddress: '',
        aptSuite: '',
        city: '',
        state: '',
        zipCode: ''
    };

    useEffect(() => {
        if (initialAddress && formikRef.current) {
            formikRef.current.setFieldValue('streetAddress', initialAddress.streetAddress || '');
            formikRef.current.setFieldValue('aptSuite', initialAddress.aptSuite || '');
            formikRef.current.setFieldValue('city', initialAddress.city || '');
            formikRef.current.setFieldValue('state', initialAddress.state || '');
            formikRef.current.setFieldValue('zipCode', initialAddress.zipCode || '');

            if (centerlocationpin.lat && centerlocationpin.lng) {
                setSelectedLocation({
                    lat: centerlocationpin.lat,
                    lng: centerlocationpin.lng
                });
            }
        }
    }, [initialAddress, centerlocationpin]);

    useEffect(() => {
        if (isOpen && initialAddress && formikRef.current) {
            setTimeout(() => {
                if (formikRef.current) {
                    formikRef.current.setFieldValue('streetAddress', initialAddress.streetAddress || '');
                    formikRef.current.setFieldValue('aptSuite', initialAddress.aptSuite || '');
                    formikRef.current.setFieldValue('city', initialAddress.city || '');
                    formikRef.current.setFieldValue('state', initialAddress.state || '');
                    formikRef.current.setFieldValue('zipCode', initialAddress.zipCode || '');

                    if (centerlocationpin.lat && centerlocationpin.lng) {
                        setSelectedLocation({
                            lat: centerlocationpin.lat,
                            lng: centerlocationpin.lng
                        });
                    }
                }
            }, 100);
        }
    }, [isOpen, initialAddress, centerlocationpin]);

    const mapContainerStyle = {
        width: '100%',
        height: '100%',
        borderRadius: '0.5rem'
    };

    const { isLoaded, loadError } = useJsApiLoader({
        googleMapsApiKey: GOOGLE_MAPS_API_KEY,
        libraries: GOOGLE_MAPS_LIBRARIES
    });

    const handlePlaceSelect = (place) => {
        if (!place.geometry) {
            return;
        }

        const { lat, lng } = place.geometry.location;
        setSelectedLocation({ lat: lat(), lng: lng() });

        let streetNumber = '';
        let streetName = '';
        let streetAddress = place.formatted_address;
        let aptSuite = '';
        let city = '';
        let state = '';
        let zipCode = '';

        if (place.address_components && place.address_components.length > 0) {
            place.address_components.forEach(component => {
                const types = component.types;

                if (types.includes('street_number')) {
                    streetNumber = component.long_name;
                }
                if (types.includes('route') || types.includes('landmark')) {
                    streetName = component.long_name;
                }
                if (types.includes('premise')) {
                    aptSuite = component.long_name;
                }
                if (types.includes('administrative_area_level_3') || types.includes('locality')) {
                    city = component.long_name;
                }
                if (types.includes('administrative_area_level_1')) {
                    state = component.short_name;
                }
                if (types.includes('postal_code')) {
                    zipCode = component.long_name;
                }
            });
        } else {
            const formattedAddress = place.formatted_address || '';

            if (formikRef.current) {
                formikRef.current.setFieldValue('streetAddress', formattedAddress);
                formikRef.current.setFieldValue('aptSuite', '');
                formikRef.current.setFieldValue('city', '');
                formikRef.current.setFieldValue('state', '');
                formikRef.current.setFieldValue('zipCode', '');
            }
        }

        if (formikRef.current) {
            formikRef.current.setFieldValue('streetAddress', `${streetName} ${streetAddress}`.trim());
            formikRef.current.setFieldValue('aptSuite', `${streetNumber} ${aptSuite}`.trim());
            formikRef.current.setFieldValue('city', city);
            formikRef.current.setFieldValue('state', state);
            formikRef.current.setFieldValue('zipCode', zipCode);
        }

        if (formikRef.current) {
            formikRef.current.setTouched({});
        }

        if (mapRef.current) {
            mapRef.current.panTo({ lat: lat(), lng: lng() });
        }
    };

    const handleMapClick = (event) => {
        setSelectedLocation({
            lat: event.latLng.lat(),
            lng: event.latLng.lng()
        });

        if (window.google) {
            const geocoder = new window.google.maps.Geocoder();
            geocoder.geocode({ location: event.latLng }, (results, status) => {
                if (status === 'OK' && results[0]) {

                    const addressComponents = results[0].address_components;

                    let streetNumber = '';
                    let streetName = '';
                    let streetAddress = results[0].formatted_address;
                    let aptSuite = '';
                    let city = '';
                    let state = '';
                    let zipCode = '';

                    addressComponents.forEach(component => {

                        const types = component.types;
                        if (types.includes('street_number')) {
                            streetNumber = component.long_name;
                        }
                        if (types.includes('route') || types.includes('landmark')) {
                            streetName = component.long_name;
                        }
                        if (types.includes('premise')) {
                            aptSuite = component.long_name;
                        }
                        if (types.includes('administrative_area_level_3') || types.includes('locality')) {
                            city = component.long_name;
                        }
                        if (types.includes('administrative_area_level_1')) {
                            state = component.short_name;
                        }
                        if (types.includes('postal_code')) {
                            zipCode = component.long_name;
                        }
                    });

                    if (formikRef.current) {
                        formikRef.current.setFieldValue('streetAddress', `${streetName} ${streetAddress}`.trim());
                        formikRef.current.setFieldValue('aptSuite', `${streetNumber} ${aptSuite}`.trim());
                        formikRef.current.setFieldValue('city', city);
                        formikRef.current.setFieldValue('state', state);
                        formikRef.current.setFieldValue('zipCode', zipCode);
                    }

                    if (formikRef.current) {
                        formikRef.current.setTouched({});
                    }
                }
            });
        }
    };

    const handleMapLoad = (map) => {
        mapRef.current = map;
    };

    const handleSave = (values) => {
        if (!selectedLocation) {
            toast.error('Please select a location on the map or search for an address');
            return;
        }

        const addressParts = [];

        const aptSuiteNum = values.aptSuite ? values.aptSuite.trim() : '';
        const streetAddr = values.streetAddress ? values.streetAddress.trim() : '';
        
        if (aptSuiteNum && !streetAddr.includes(aptSuiteNum)) {
            addressParts.push(aptSuiteNum);
        }

        if (streetAddr) {
            addressParts.push(streetAddr);
        }

        const fullAddress = addressParts.join(', ');

        const updatedSelectedLocation = {
            lat: selectedLocation.lat,
            lng: selectedLocation.lng,
            streetAddress: values.streetAddress || '',
            aptSuite: values.aptSuite || '',
        };

        canterlocatlon({
            lat: selectedLocation.lat,
            lng: selectedLocation.lng,
            ...updatedSelectedLocation
        });

        onLocationSelect(fullAddress);
    };

    useEffect(() => {
        if (isOpen && mapRef.current && isLoaded) {
            setTimeout(() => {
                const map = mapRef.current;
                if (map && window.google) {
                    window.google.maps.event.trigger(map, 'resize');
                    map.setCenter(selectedLocation);
                }
            }, 300);
        }
    }, [isOpen, selectedLocation, isLoaded]);

    if (!isOpen) return null;
    if (loadError) return <div>Error loading map</div>;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-99 flex items-center justify-center p-4 overflow-y-auto">
            <div className="bg-white w-full max-w-5xl rounded-lg overflow-hidden">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 lg:h-fit h-[80vh] overflow-y-auto">
                    <div className="col-span-12 lg:col-span-6 relative">
                        <div className="relative lg:h-full h-[30vh]">
                            {!isLoaded && (
                                <div className="absolute inset-0 flex items-center justify-center bg-gray-100 rounded-lg z-10">
                                    <div className="text-center">
                                        <div className="animate-spin h-8 w-8 border-2 border-b-0 rounded-full mx-auto mb-2 border-gray-800"></div>
                                        <div>Loading map...</div>
                                    </div>
                                </div>
                            )}
                            {isLoaded && (
                                <GoogleMap
                                    mapContainerStyle={mapContainerStyle}
                                    center={selectedLocation}
                                    zoom={15}
                                    onClick={handleMapClick}
                                    onLoad={handleMapLoad}
                                    options={{
                                        zoomControl: true,
                                        streetViewControl: false,
                                        mapTypeControl: false,
                                        fullscreenControl: false,
                                    }}
                                >
                                    {selectedLocation && <Marker position={selectedLocation} />}
                                </GoogleMap>
                            )}
                        </div>
                    </div>

                    <div className="col-span-12 lg:col-span-6 p-5 rounded-lg space-y-4">
                        <div className="flex justify-between items-center">
                            <h2 className="md:text-lg text-base font-semibold">Studio Location</h2>
                            <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
                                <FiX size={20} />
                            </button>
                        </div>

                        <Formik
                            innerRef={formikRef}
                            key={JSON.stringify(initialAddress)}
                            initialValues={initialAddress || initialAddressValues}
                            validationSchema={addressValidationSchema}
                            onSubmit={handleSave}
                            enableReinitialize={true}
                        >
                            {({ values, errors, touched, handleChange, handleBlur }) => (
                                <Form className="space-y-4 text-sm">
                                    <div>
                                        <label className="block text-sm font-medium mb-1">Street Address *</label>
                                        <Autocomplete
                                            onPlaceSelected={handlePlaceSelect}
                                            options={{
                                                types: ['geocode', 'establishment'],
                                                fields: ['address_components', 'geometry', 'formatted_address']
                                            }}
                                            placeholder="Enter street address"
                                            className={`w-full px-3 py-2 border rounded-lg focus:outline-none ${touched.streetAddress && errors.streetAddress
                                                ? 'border-red-500'
                                                : 'border-gray-300 focus:border-gray-400'
                                                }`}
                                            apiKey={GOOGLE_MAPS_API_KEY}
                                            value={values.streetAddress}
                                            onChange={(e) => handleChange({ target: { name: 'streetAddress', value: e.target.value } })}
                                        />
                                        {touched.streetAddress && errors.streetAddress && (
                                            <div className="text-red-500 text-xs mt-1">{errors.streetAddress}</div>
                                        )}
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium mb-1">Apt, Suite, etc.</label>
                                        <input
                                            type="text"
                                            name="aptSuite"
                                            placeholder="Enter apartment or suite number (optional)"
                                            value={values.aptSuite}
                                            onChange={handleChange}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-gray-400"
                                        />
                                    </div>

                                    <div className="grid md:grid-cols-2 grid-cols-1 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium mb-1">City *</label>
                                            <input
                                                type="text"
                                                name="city"
                                                placeholder="Enter city"
                                                value={values.city}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                className={`w-full px-3 py-2 border rounded-lg focus:outline-none ${touched.city && errors.city
                                                    ? 'border-red-500'
                                                    : 'border-gray-300 focus:border-gray-400'
                                                    }`}
                                            />
                                            {touched.city && errors.city && (
                                                <div className="text-red-500 text-xs mt-1">{errors.city}</div>
                                            )}
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium mb-1">State *</label>
                                            <input
                                                type="text"
                                                name="state"
                                                placeholder="Enter state"
                                                value={values.state}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                className={`w-full px-3 py-2 border rounded-lg focus:outline-none ${touched.state && errors.state
                                                    ? 'border-red-500'
                                                    : 'border-gray-300 focus:border-gray-400'
                                                    }`}
                                            />
                                            {touched.state && errors.state && (
                                                <div className="text-red-500 text-xs mt-1">{errors.state}</div>
                                            )}
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium mb-1">Zip Code *</label>
                                        <input
                                            type="text"
                                            name="zipCode"
                                            placeholder="Enter zip code"
                                            value={values.zipCode}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            className={`w-full px-3 py-2 border rounded-lg focus:outline-none ${touched.zipCode && errors.zipCode
                                                ? 'border-red-500'
                                                : 'border-gray-300 focus:border-gray-400'
                                                }`}
                                        />
                                        {touched.zipCode && errors.zipCode && (
                                            <div className="text-red-500 text-xs mt-1">{errors.zipCode}</div>
                                        )}
                                    </div>

                                    <div className='pt-3'>
                                        <button
                                            type="submit"
                                            className={`w-full py-3 px-4 rounded-lg bg-black text-white hover:bg-gray-800`}
                                        >
                                            Save
                                        </button>
                                    </div>
                                </Form>
                            )}
                        </Formik>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default function MyStudio() {
    const [isLocationModalOpen, setIsLocationModalOpen] = useState(false);
    const [studio, setStudio] = useState({});
    const setFieldValueRef = useRef(null);
    const [status, setStatus] = useState([]);
    const [mapCenter, setMapCenter] = useState({});
    const [parsedAddressForModal, setParsedAddressForModal] = useState(null);
    const [initialLocationStructuredAddress, setInitialLocationStructuredAddress] = useState(null);
    const navigate = useNavigate();    

    const getProfile = () => {
        getProfileAPI()
            .then((res) => {
                if (res?.data?.status === 1) {
                    const studioData = res?.data?.data?.studio || {};
                    setStudio(studioData);
                    setStatus(res?.data?.data?.available_statuses || []);
                    setMapCenter({
                        lat: studioData.studio_latitude,
                        lng: studioData.studio_longitude,
                    });
                } else {
                    toast.error(res?.data?.message);
                }
            })
            .catch((err) => {
                let message = err?.response?.data?.message || 'Failed to fetch studio';
                toast.error(message);
                if (err?.response?.status === 401) {
                    localStorage.removeItem('hoast_token')
                    localStorage.removeItem('hoast_refresh_token')
                    navigate('/login');
                }
            })
    }

    useEffect(() => {
        getProfile();
    }, []);

    const areAllValuesFilled = (values) => {
        return values.studioName &&
            values.status &&
            values.buzzerNumber &&
            values.about &&
            values.about.trim().length >= 10
    }

    const containerStyle = {
        height: "120px",
        width: "100%",
        borderRadius: "14px",
    };

    const { isLoaded: isMainMapLoaded } = useJsApiLoader({
        googleMapsApiKey: GOOGLE_MAPS_API_KEY,
        libraries: GOOGLE_MAPS_LIBRARIES
    });

    const convertCoordinatesToAddress = (lat, lng) => {
        if (isGoogleMapsReady()) {
            try {
                const geocoder = new window.google.maps.Geocoder();
                geocoder.geocode({ location: { lat, lng } }, (results, status) => {
                    if (status === 'OK' && results[0]) {
                        const formattedAddress = results[0].formatted_address;

                        const addressComponents = results[0].address_components;
                        let streetNumber = '';
                        let streetName = '';
                        let aptSuite = '';
                        let city = '';
                        let state = '';
                        let zipCode = '';

                        addressComponents.forEach(component => {
                            const types = component.types;
                            if (types.includes('street_number')) {
                                streetNumber = component.long_name;
                            }
                            if (types.includes('route')) {
                                streetName = component.long_name;
                            }
                            if (types.includes('subpremise') || types.includes('premise')) {
                                aptSuite = component.long_name;
                            }
                            if (types.includes('locality') || types.includes('postal_town') || types.includes('sublocality')) {
                                city = component.long_name;
                            }
                            if (types.includes('administrative_area_level_1')) {
                                state = component.long_name;
                            }
                            if (types.includes('postal_code')) {
                                zipCode = component.long_name;
                            }
                        });

                        const structuredAddress = {
                            streetAddress: streetNumber && streetName ? `${streetNumber} ${streetName}`.trim() : (streetName || formattedAddress),
                            aptSuite: aptSuite || '',
                            city: city || 'Surat',
                            state: state || 'Gujarat',
                            zipCode: zipCode || '395001'
                        };

                        setParsedAddressForModal(structuredAddress);
                    }
                });
            } catch (error) {
                console.error('Geocoder error:', error);
            }
        }
    };

    useEffect(() => {
        if (isMainMapLoaded && isGoogleMapsReady()) {
            convertCoordinatesToAddress(mapCenter.lat, mapCenter.lng);
        }
    }, [isMainMapLoaded, mapCenter]);

    const parseAddressToComponents = (fullAddress) => {
        if (!fullAddress || !fullAddress.trim()) return null;

        if (isGoogleMapsReady()) {
            return new Promise((resolve) => {
                try {
                    const geocoder = new window.google.maps.Geocoder();
                    geocoder.geocode({ address: fullAddress }, (results, status) => {
                        if (status === 'OK' && results[0]) {
                            const addressComponents = results[0].address_components;
                            let streetNumber = '';
                            let streetName = '';
                            let aptSuite = '';
                            let city = '';
                            let state = '';
                            let zipCode = '';

                            addressComponents.forEach(component => {
                                const types = component.types;
                                if (types.includes('street_number')) {
                                    streetNumber = component.long_name;
                                }
                                if (types.includes('route')) {
                                    streetName = component.long_name;
                                }
                                if (types.includes('subpremise') || types.includes('premise')) {
                                    aptSuite = component.long_name;
                                }
                                if (types.includes('locality') || types.includes('postal_town') || types.includes('sublocality')) {
                                    city = component.long_name;
                                }
                                if (types.includes('administrative_area_level_1')) {
                                    state = component.long_name;
                                }
                                if (types.includes('postal_code')) {
                                    zipCode = component.long_name;
                                }
                            });

                            if (!streetName && !city) {
                                const parts = fullAddress.split(',').map(part => part.trim());
                                if (parts.length >= 3) {
                                    streetName = parts[0] || '';
                                    city = parts[1] || '';
                                    state = parts[2] || '';
                                    zipCode = parts[3] || '';
                                }
                            }

                            const parsedAddress = {
                                streetAddress: streetNumber && streetName ? `${streetNumber} ${streetName}`.trim() : (streetName || fullAddress),
                                aptSuite: aptSuite || '',
                                city: city || 'Surat',
                                state: state || 'Gujarat',
                                zipCode: zipCode || '395001',
                                lat: results[0].geometry.location.lat(),
                                lng: results[0].geometry.location.lng()
                            };

                            resolve(parsedAddress);
                        } else {
                            const parts = fullAddress.split(',').map(part => part.trim());
                            if (parts.length >= 3) {
                                resolve({
                                    streetAddress: parts[0] || '',
                                    aptSuite: '',
                                    city: parts[1] || 'Surat',
                                    state: parts[2] || 'Gujarat',
                                    zipCode: parts[3] || '395001'
                                });
                            } else {
                                resolve({
                                    streetAddress: fullAddress,
                                    aptSuite: '',
                                    city: 'Surat',
                                    state: 'Gujarat',
                                    zipCode: '395001'
                                });
                            }
                        }
                    });
                } catch (error) {
                    console.error('Geocoder error:', error);
                    resolve({
                        streetAddress: fullAddress,
                        aptSuite: '',
                        city: 'Surat',
                        state: 'Gujarat',
                        zipCode: '395001'
                    });
                }
            });
        }

        const parts = fullAddress.split(',').map(part => part.trim());
        if (parts.length >= 3) {
            return Promise.resolve({
                streetAddress: parts[0] || '',
                aptSuite: '',
                city: parts[1] || '',
                state: parts[2] || '',
                zipCode: parts[3] || ''
            });
        }

        return Promise.resolve({
            streetAddress: fullAddress,
            aptSuite: '',
            city: 'Surat',
            state: 'Gujarat',
            zipCode: '395001'
        });
    };

    const openLocationModal = async (currentFormLocation = '') => {
        const currentLocation = currentFormLocation || studio?.studio_street || '';
        const plusCodeRegex = /[A-Z0-9]{4,}\+[A-Z0-9]{2,}(,\s)?/g;
        const finalAddress = currentLocation.replace(plusCodeRegex, '').trim();

        if (finalAddress && finalAddress.trim()) {
            try {
                const parsedAddress = await parseAddressToComponents(finalAddress);

                if (parsedAddress && Object.values(parsedAddress).some(val => val && val.trim())) {
                    setParsedAddressForModal(parsedAddress);
                    setIsLocationModalOpen(true);
                    return;
                }
            } catch (error) {
                console.error('Error parsing address:', error);
            }
        }

        setParsedAddressForModal(null);
        setIsLocationModalOpen(true);
    };

    const closeLocationModal = () => {
        setIsLocationModalOpen(false);
    };

    const addSocialLink = (setFieldValue, values) => {
        const newLink = { platform: 'custom', url: '', icon: FiLink };
        const currentLinks = values.socials || [];
        setFieldValue('socials', [newLink, ...currentLinks]);
    };

    const removeSocialLink = (setFieldValue, values, index) => {
        const currentLinks = values.socials || [];
        const updatedLinks = currentLinks.filter((_, i) => i !== index);
        setFieldValue('socials', updatedLinks);
    };

    const handleSubmit = (values, { setSubmitting }) => {

        const formData = new FormData();
        formData.append('studio_id', studio.id || '');
        formData.append('studio_name', values.studioName || '');
        const selectedStatus = status?.find(s => s.value === values.status);
        formData.append('studio_status_id', selectedStatus?.id || '');
        formData.append('studio_street', values.location || '');
        formData.append('studio_landmark', parsedAddressForModal?.streetAddress || '');
        formData.append('studio_city', parsedAddressForModal?.city || '');
        formData.append('studio_zipcode', parsedAddressForModal?.zipCode || '');
        formData.append('studio_state', parsedAddressForModal?.state || '');
        formData.append('studio_buzzer_number', values.buzzerNumber || '');
        formData.append('studio_about', values.about || '');
        formData.append('studio_latitude', mapCenter.lat || '');
        formData.append('studio_longitude', mapCenter?.lng || '');

        const socialLinks = values.socials
            .filter(social => social.url && social.url.trim() !== '')
            .map(social => ({
                studio_platform: social.platform,
                studio_platform_url: social.url
            }));
        formData.append('studio_socials', JSON.stringify(socialLinks));

        updateStudioAPI(formData)
            .then((res) => {
                if (res?.data?.status === 1) {
                    toast.success('Studio updated successfully!');
                    getProfile();
                } else {
                    toast.error(res?.data?.message || 'Failed to update studio');
                }
            })
            .catch((err) => {
                let message = err?.response?.data?.message || 'Failed to update studio';
                toast.error(message);
                if (err?.response?.status === 401) {
                    localStorage.removeItem('hoast_token');
                    localStorage.removeItem('hoast_refresh_token');
                    navigate('/login');
                }
            })
            .finally(() => {
                setSubmitting(false);
            });
    };

    return (
        <div className="bg-white">
            <div className="max-w-4xl px-2">
                <Formik
                    initialValues={{
                        ...getInitialValues(studio),
                        location: initialLocationStructuredAddress !== null ? initialLocationStructuredAddress : studio?.studio_street
                    }}
                    validationSchema={studioValidationSchema}
                    onSubmit={handleSubmit}
                    validateOnBlur={true}
                    validateOnChange={true}
                    validateOnMount={false}
                    validateOnSubmit={true}
                    enableReinitialize={true}
                >
                    {({ values, errors, touched, handleChange, handleBlur, handleSubmit, isSubmitting, setFieldValue, submitCount, isValid }) => {
                        setFieldValueRef.current = setFieldValue;
                        const allFilled = areAllValuesFilled(values);
                        const hasValidationErrors = Object.keys(errors).length > 0;

                        return (
                            <Form onSubmit={handleSubmit}>
                                <div className="space-y-6">
                                    <div className="flex justify-end">
                                        <button
                                            type="submit"
                                            disabled={isSubmitting}
                                            className={`px-8 sm:w-[200px] w-full h-[52px] rounded-xl font-medium transition-colors ${isSubmitting
                                                ? 'bg-gray-400 cursor-not-allowed text-white'
                                                : (allFilled && !hasValidationErrors)
                                                    ? 'bg-[#212121] text-white hover:bg-[#1a1a1a]'
                                                    : 'bg-[#F0F1F3] text-black cursor-not-allowed'
                                                }`}
                                        >
                                            {isSubmitting ? (
                                                <EllipsisLoader color="white" />
                                            ) : (
                                                'Save'
                                            )}
                                        </button>
                                    </div>
                                    <div className="grid grid-cols-12 text-sm items-start gap-4">
                                        <label className="col-span-12 sm:col-span-4 text-sm font-medium text-gray-700">
                                            Studio Name
                                        </label>
                                        <div className="col-span-12 sm:col-span-8">
                                            <input
                                                type="text"
                                                name="studioName"
                                                placeholder="Enter studio name"
                                                value={values.studioName}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                className={`w-full border p-3 rounded-lg focus:outline-none ${(touched.studioName || submitCount > 0) && errors.studioName
                                                    ? 'border-red-500'
                                                    : 'border-gray-300 focus:border-gray-400'
                                                    }`}
                                            />
                                            {(touched.studioName || submitCount > 0) && errors.studioName && (
                                                <div className="text-red-500 text-xs mt-1">{errors.studioName}</div>
                                            )}
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-12 text-sm items-start gap-4">
                                        <label className="col-span-12 sm:col-span-4 text-sm font-medium text-gray-700 mt-2">
                                            Status
                                        </label>
                                        <div className="col-span-12 sm:col-span-8">
                                            <CustomSelectStatus
                                                name="status"
                                                value={values.status}
                                                onChange={(name, value) => setFieldValue("status", value)}
                                                options={status?.map(status => ({
                                                    label: status.value,
                                                    value: status.value,
                                                    color: status.color
                                                })) || []}
                                                error={errors.status}
                                                touched={touched.status || submitCount > 0}
                                            />
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-12 text-sm items-start gap-4">
                                        <label className="col-span-12 sm:col-span-4 text-sm font-medium text-gray-700 mt-2">
                                            Location
                                        </label>
                                        <div className="col-span-12 sm:col-span-8 space-y-3">
                                            <div className="relative">
                                                {!isMainMapLoaded && (
                                                    <div className="absolute inset-0 bg-gray-100 rounded-lg flex items-center justify-center z-10">
                                                        <div className="text-center">
                                                            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-800 mx-auto mb-1"></div>
                                                            <div className="text-gray-600 text-sm">Loading map...</div>
                                                        </div>
                                                    </div>
                                                )}

                                                {isMainMapLoaded && (
                                                    <GoogleMap
                                                        mapContainerStyle={containerStyle}
                                                        center={mapCenter}
                                                        zoom={16}
                                                        options={{
                                                            zoomControl: false,
                                                            streetViewControl: false,
                                                            mapTypeControl: false,
                                                            fullscreenControl: false,
                                                        }}
                                                    >
                                                        <Marker position={mapCenter} />
                                                    </GoogleMap>
                                                )}
                                            </div>
                                            <div className="flex items-center justify-between">
                                                <button
                                                    type="button"
                                                    onClick={() => openLocationModal(values.location)}
                                                    className="text-start flex items-center space-x-2 text-[#24B565] transition-colors"
                                                >
                                                    <FiMapPin className="text-sm" />
                                                    <span>Select Location</span>
                                                </button>
                                            </div>

                                            <div className="relative">
                                                <input
                                                    type="text"
                                                    name="location"
                                                    placeholder="Your location"
                                                    value={values.location}
                                                    onBlur={handleBlur}
                                                    className={`w-full border p-3 rounded-lg focus:outline-none ${(touched.location || submitCount > 0) && errors.location
                                                        ? 'border-red-500'
                                                        : 'border-gray-300 focus:border-gray-400'
                                                        }`}
                                                    disabled
                                                />
                                            </div>
                                            {(touched.location || submitCount > 0) && errors.location && (
                                                <div className="text-red-500 text-xs mt-1">{errors.location}</div>
                                            )}
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-12 text-sm items-start gap-4">
                                        <label className="col-span-12 sm:col-span-4 text-sm font-medium text-gray-700 mt-2">
                                            Buzzer Number
                                        </label>
                                        <div className="col-span-12 sm:col-span-8">
                                            <input
                                                type="text"
                                                name="buzzerNumber"
                                                placeholder="Enter buzzer number"
                                                value={values.buzzerNumber}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                className={`w-full border p-3 rounded-lg focus:outline-none ${(touched.buzzerNumber || submitCount > 0) && errors.buzzerNumber
                                                    ? 'border-red-500'
                                                    : 'border-gray-300 focus:border-gray-400'
                                                    }`}
                                            />
                                            {(touched.buzzerNumber || submitCount > 0) && errors.buzzerNumber && (
                                                <div className="text-red-500 text-xs mt-1">{errors.buzzerNumber}</div>
                                            )}
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-12 text-sm items-start gap-4">
                                        <label className="col-span-12 sm:col-span-4 text-sm font-medium text-gray-700 mt-2">
                                            About
                                        </label>
                                        <div className="col-span-12 sm:col-span-8">
                                            <div className="">
                                                <textarea
                                                    name="about"
                                                    placeholder="Enter studio description"
                                                    value={values.about}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    rows={6}
                                                    className={`w-full border px-3 py-2 rounded-lg resize-none focus:outline-none ${(touched.about || submitCount > 0) && errors.about
                                                        ? 'border-red-500'
                                                        : 'border-gray-300 focus:border-gray-400'
                                                        }`}
                                                />
                                            </div>
                                            <div className="flex justify-between items-center mt-1">
                                                <div>
                                                    {(touched.about || submitCount > 0) && errors.about && (
                                                        <div className="text-red-500 text-xs">{errors.about}</div>
                                                    )}
                                                </div>
                                                <div className="text-xs text-gray-500">
                                                    {values.about?.length || 0}/1000
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-12 text-sm items-start gap-4">
                                        <label className="col-span-12 sm:col-span-4 text-sm font-medium text-gray-700 mt-2">
                                            Socials
                                        </label>
                                        <div className="col-span-12 sm:col-span-8 space-y-3">
                                            <div className='flex items-center justify-end'>
                                                <button
                                                    type="button"
                                                    onClick={() => addSocialLink(setFieldValue, values)}
                                                    className="flex items-center space-x-2 text-green-600 hover:text-green-700 transition-colors"
                                                >
                                                    <FiPlus className="text-sm" />
                                                    <span>Add New</span>
                                                </button>
                                            </div>
                                            {(values.socials || []).map((social, index) => (
                                                <Field name={`socials[${index}].url`} key={`social-${index}`}>
                                                    {({ field, meta }) => (
                                                        <div className="mb-3">
                                                            <div className="flex gap-2 items-center relative">
                                                                <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
                                                                    {React.createElement(getSocialIcon(social.platform), {
                                                                        className: "text-gray-500 text-lg"
                                                                    })}
                                                                </div>
                                                                <input
                                                                    {...field}
                                                                    type="text"
                                                                    placeholder={`https://${social.platform}.com`}
                                                                    className={`w-full pl-10 pr-3 py-3 rounded-lg border focus:outline-none ${meta.touched && meta.error
                                                                        ? 'border-red-500'
                                                                        : 'border-gray-300 focus:border-gray-400'
                                                                        }`}
                                                                />
                                                                {social.platform === 'custom' && (
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => removeSocialLink(setFieldValue, values, index)}
                                                                        className="p-3 -mt-1 border text-red-500 rounded-lg shadow hover:text-red-700 transition-colors"
                                                                        aria-label="Remove social link"
                                                                    >
                                                                        <FiX size={16} />
                                                                    </button>
                                                                )}
                                                            </div>
                                                            {meta.touched && meta.error && (
                                                                <div className="text-red-500 text-xs mt-1">{meta.error}</div>
                                                            )}
                                                        </div>
                                                    )}
                                                </Field>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </Form>
                        );
                    }}
                </Formik>

                <LocationModal
                    isOpen={isLocationModalOpen}
                    onClose={closeLocationModal}
                    onLocationSelect={(fullAddress, structuredAddress) => {
                        if (setFieldValueRef.current) {
                            setFieldValueRef.current('location', fullAddress);
                        }
                        setInitialLocationStructuredAddress(fullAddress);
                        closeLocationModal();
                    }}
                    canterlocatlon={(location) => {
                        setMapCenter({
                            lat: location.lat,
                            lng: location.lng
                        });
                    }}
                    initialAddress={parsedAddressForModal}
                    centerlocationpin={mapCenter}
                />
            </div>
        </div>
    );
}